import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Copy, Loader2, MessageSquare, Star } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useTestimonials, useCreateTestimonialRequest } from "@/hooks/useTestimonials";
import type { Lead } from "@/hooks/useLeads";

interface Props {
  lead: Lead;
  /** Project to attach the testimonial to (first linked project). */
  clientProjectId?: string | null;
}

/**
 * Post-delivery step: request a testimonial. Creates the pending
 * `testimonials` row (which mints the /feedback/:token link) and offers
 * copy / WhatsApp — the only way this flow was reachable before was hand-
 * crafting the row in the database.
 */
export function TestimonialRequestCard({ lead, clientProjectId }: Props) {
  const { data: all = [], isLoading } = useTestimonials();
  const create = useCreateTestimonialRequest();

  const mine = all.filter(
    (t) =>
      (lead.client_id && t.client_id === lead.client_id) ||
      (clientProjectId && t.client_project_id === clientProjectId),
  );
  const pending = mine.find((t) => !t.submitted_at && t.public_token);
  const submitted = mine.find((t) => !!t.submitted_at);

  const link = (token: string) => `${window.location.origin}/feedback/${token}`;

  const request = () =>
    create.mutate(
      {
        client_project_id: clientProjectId ?? undefined,
        client_id: lead.client_id ?? undefined,
        author_name: lead.name,
      },
      {
        onSuccess: () => toast.success("Link de depoimento gerado! Envie ao cliente."),
        onError: (e) => toast.error("Erro ao gerar link: " + e.message),
      },
    );

  const shareWhatsApp = (token: string) => {
    if (!lead.phone) return;
    const phone = lead.phone.replace(/\D/g, "");
    const first = lead.name.split(" ")[0];
    const text = [
      `Olá ${first}! Foi um prazer trabalhar com você.`,
      "Poderia deixar um depoimento rápido sobre o projeto? Leva menos de 1 minuto:",
      link(token),
      "",
      "Muito obrigado!",
    ].join("\n");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <Card className="rounded-2xl shadow-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 font-display text-base">
          <Star className="h-4 w-4 text-warn" /> Depoimento do cliente
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : submitted ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Badge
                className={submitted.approved ? "bg-ok-soft text-ok" : "bg-warn-soft text-warn"}
              >
                {submitted.approved ? "Aprovado" : "Aguardando aprovação"}
              </Badge>
              {submitted.submitted_at && (
                <span className="text-xs text-muted-foreground">
                  recebido em{" "}
                  {format(new Date(submitted.submitted_at), "dd/MM/yyyy", { locale: ptBR })}
                </span>
              )}
            </div>
            <p className="whitespace-pre-wrap text-[13px] leading-[1.6] text-muted-foreground">
              “{submitted.quote}”
            </p>
            {!submitted.approved && (
              <p className="text-xs text-muted-foreground">
                Aprove em Conteúdo → Depoimentos para publicar no site.
              </p>
            )}
          </div>
        ) : pending ? (
          <div className="space-y-2">
            <p className="text-[13px] text-muted-foreground">
              Link gerado. Envie ao cliente e acompanhe aqui quando responder.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(link(pending.public_token!));
                  toast.success("Link copiado");
                }}
              >
                <Copy className="mr-1.5 h-3.5 w-3.5" /> Copiar link
              </Button>
              {lead.phone && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => shareWhatsApp(pending.public_token!)}
                >
                  <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> Enviar por WhatsApp
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-[13px] text-muted-foreground">
              Projeto entregue — peça um depoimento enquanto a experiência está fresca.
            </p>
            <Button size="sm" onClick={request} disabled={create.isPending}>
              {create.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Solicitar depoimento
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
